import { type Component, type Ref, computed, ref, watchEffect } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { iconModules } from '@ds/icons/backmarket.icons'

import { RenderDynamicIconState } from '../models/dynamic-icon-state'

function keyExists(icon: string): icon is keyof typeof iconModules {
  return Object.keys(iconModules).includes(icon)
}

async function importIconComponent(icon: string): Promise<Component> {
  if (!keyExists(icon)) {
    throw new Error(`[CMS Icon] Could not find path to import icon ${icon}`)
  }

  const importIconFunction = iconModules[icon]
  const iconModule = (await importIconFunction()) as {
    [icon: string]: Component
  }

  return iconModule[icon]
}

export function useImportIconComponent(iconName: Ref<string>) {
  const logger = useLogger()

  // NB: we avoid using a ref since we do not need this to be reactive and it negatively impacts performance
  // This is also prevents the related warning: "Vue received a Component that was made a reactive object"
  // See issue discussed here https://github.com/vuejs/core/issues/11921
  // NB2: if we need this to be reactive, we could use shallowRef
  const icon: { component: Component | null } = { component: null }

  const renderIconState = ref<RenderDynamicIconState>(
    RenderDynamicIconState.Pending,
  )

  const success = computed(
    () => renderIconState.value === RenderDynamicIconState.Success,
  )

  const pending = computed(
    () => renderIconState.value === RenderDynamicIconState.Pending,
  )

  watchEffect(async () => {
    try {
      icon.component = await importIconComponent(iconName.value)
      renderIconState.value = RenderDynamicIconState.Success
    } catch (err) {
      logger.error(
        `[CMS Icon] icon ${
          iconName.value
        } does not exist in the Design System. Error ${
          (err as Error)?.message
        }`,
        { owners: ['bot-squad-content-front'] },
      )
      renderIconState.value = RenderDynamicIconState.Error
    }
  })

  return { icon, success, pending }
}
